import { useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type Tone = "primary" | "accent" | "success" | "warning" | "danger" | "muted";

const toneStyles: Record<Tone, { card: string; iconBg: string; value: string }> = {
  primary: {
    card: "bg-primary-50/60 border-primary-200/60",
    iconBg: "bg-primary text-primary-foreground",
    value: "text-primary-700",
  },
  accent: {
    card: "bg-[hsl(200_95%_97%)] border-[hsl(200_95%_88%)]",
    iconBg: "bg-accent text-accent-foreground",
    value: "text-[hsl(200_95%_30%)]",
  },
  success: {
    card: "bg-[hsl(150_55%_96%)] border-[hsl(150_50%_86%)]",
    iconBg: "bg-success text-success-foreground",
    value: "text-[hsl(150_65%_25%)]",
  },
  warning: {
    card: "bg-[hsl(36_92%_96%)] border-[hsl(36_85%_85%)]",
    iconBg: "bg-warning text-warning-foreground",
    value: "text-[hsl(36_92%_30%)]",
  },
  danger: {
    card: "bg-[hsl(0_75%_97%)] border-[hsl(0_75%_88%)]",
    iconBg: "bg-destructive text-destructive-foreground",
    value: "text-[hsl(0_75%_35%)]",
  },
  muted: {
    card: "bg-muted/50 border-border",
    iconBg: "bg-muted-foreground/20 text-foreground",
    value: "text-foreground",
  },
};

type Props = {
  label: string;
  value: number | string;
  hint?: string;
  tone?: Tone;
  icon?: LucideIcon;
  to?: string;
};

export function StatCard({ label, value, hint, tone = "primary", icon: Icon, to }: Props) {
  const nav = useNavigate();
  const t = toneStyles[tone];

  return (
    <button
      type="button"
      onClick={to ? () => nav(to) : undefined}
      className={cn(
        "group text-left w-full rounded-lg border p-4 transition-all",
        "hover:-translate-y-0.5 hover:shadow-lifted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        t.card,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider truncate">
            {label}
          </div>
          <div className={cn("mt-1 font-serif text-4xl leading-none tabular-nums", t.value)}>
            {typeof value === "number" ? value.toLocaleString("vi-VN") : value}
          </div>
          {hint && <div className="mt-2 text-xs text-muted-foreground truncate">{hint}</div>}
        </div>
        {Icon && (
          <div
            className={cn(
              "h-10 w-10 shrink-0 rounded-md grid place-items-center shadow-soft",
              t.iconBg,
            )}
          >
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </div>
        )}
      </div>
    </button>
  );
}
